import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useAppSelector from 'hooks/useAppSelector';
import { FooterInfo } from 'microshop-api';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import { getIcon } from 'utils/icons';
import Text from './Text';

const Footer = ({ footerInfo }: { footerInfo?: FooterInfo | null }) => {
    const { t } = useTranslation();
    const { theme, shop } = useAppSelector(({ shop }) => shop);

    if (!footerInfo) return null;

    const { brandName, tagLine, copyrightText, socialLinks, footerLinkColumns, paymentMethods } = footerInfo;
    const linkColumnCount = Math.min(footerLinkColumns?.length ?? 0, 5);
    const totalColumns = 1 + linkColumnCount + (paymentMethods ? 1 : 0);

    return (
        <StyledFooter $bgColor={theme?.footer} $color={theme?.footerTextColor}>
            <FooterContent>
                <FooterGrid $cols={totalColumns}>
                    <BrandColumn>
                        {brandName && <BrandName>{brandName}</BrandName>}
                        {tagLine && <TagLine>{tagLine}</TagLine>}
                        {!!socialLinks?.length && (
                            <SocialRow>
                                {socialLinks.map((link, i) => {
                                    const icon = getIcon(link.icon);
                                    if (!icon) return null;
                                    return link.url ? (
                                        <SocialIcon key={i} href={link.url} target="_blank" rel="noopener noreferrer">
                                            <FontAwesomeIcon icon={icon} />
                                        </SocialIcon>
                                    ) : (
                                        <SocialIconSpan key={i}>
                                            <FontAwesomeIcon icon={icon} />
                                        </SocialIconSpan>
                                    );
                                })}
                            </SocialRow>
                        )}
                    </BrandColumn>

                    {footerLinkColumns?.slice(0, 5).map((col, i) => (
                        <FooterColumn key={i}>
                            {col.heading && <ColHeading>{col.heading}</ColHeading>}
                            {!!col.items?.length && (
                                <LinkList>
                                    {col.items.map((item, j) =>
                                        item.url ? (
                                            <li key={j}>
                                                <a href={item.url}>{item.label}</a>
                                            </li>
                                        ) : (
                                            <li key={j}>{item.label}</li>
                                        ),
                                    )}
                                </LinkList>
                            )}
                        </FooterColumn>
                    ))}
                    {/* <FooterColumn>
                        <ColHeading>{t('conditions')}</ColHeading>
                        <LinkList>
                            <li>
                                <Link to="/terms-and-conditions">{t('terms_and_conditions')}</Link>
                            </li>
                            <li>
                                <Link to="/privacy-policy">{t('privacy_policy')}</Link>
                            </li>
                            <li>
                                <a href={shop?.garanUrl || ''} target="_blank" rel="noopener noreferrer">
                                    {t('legal_guarantee_policy')}
                                </a>
                            </li>
                        </LinkList>
                    </FooterColumn> */}
                    {paymentMethods && (
                        <FooterColumn>
                            {paymentMethods.heading && <ColHeading>{paymentMethods.heading}</ColHeading>}
                            {!!paymentMethods.methods?.length && (
                                <BadgeRow>
                                    {paymentMethods.methods.map((method, i) => {
                                        const icon = getIcon(method.icon);
                                        const badge = (
                                            <PaymentBadge>
                                                {icon ? <FontAwesomeIcon icon={icon} /> : method.title}
                                            </PaymentBadge>
                                        );

                                        return method.url ? (
                                            <a key={i} href={method.url} target="_blank" rel="noopener noreferrer">
                                                {badge}
                                            </a>
                                        ) : (
                                            <span key={i}>{badge}</span>
                                        );
                                    })}
                                </BadgeRow>
                            )}
                        </FooterColumn>
                    )}
                </FooterGrid>

                <Divider $color={theme?.footerTextColor} />

                {copyrightText && <CopyrightBar>{copyrightText}</CopyrightBar>}
            </FooterContent>
        </StyledFooter>
    );
};

const StyledFooter = styled.footer<{ $bgColor?: string | null; $color?: string | null }>`
    width: 100%;
    ${({ $bgColor }) => $bgColor && `background-color: ${$bgColor};`}
    ${({ $color }) => $color && `color: ${$color};`}

    a {
        color: inherit;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const FooterContent = styled.div`
    max-width: ${({ theme }) => theme.breakpoints.xl}px;
    margin: 0 auto;
    padding: 48px 24px 24px 68px;
`;

const FooterGrid = styled.div<{ $cols: number }>`
    display: grid;
    grid-template-columns: 1.5fr ${({ $cols }) => ($cols > 1 ? `repeat(${$cols - 1}, 1fr)` : '')};
    gap: 40px;

    ${({ theme }) => theme.media.lgBelow} {
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    }

    ${({ theme }) => theme.media.smBelow} {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const BrandColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    ${({ theme }) => theme.media.smBelow} {
        grid-column: 1 / -1;
    }
`;

const BrandName = styled.h2`
    font-size: 24px;
    font-weight: 900;
    text-transform: uppercase;
    margin: 0;
`;

const TagLine = styled(Text)`
    color: rgba(153, 161, 175, 1);
    font-size: 16px;
    margin: 0;
    opacity: 0.85;
`;

const SocialRow = styled.div`
    display: flex;
    gap: 16px;
    margin-top: 30px;
`;

const socialIconBase = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-size: 32px;
    line-height: 1;
`;

const SocialIcon = styled.a`
    ${socialIconBase}
`;

const SocialIconSpan = styled.span`
    ${socialIconBase}
`;

const FooterColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ColHeading = styled.h3`
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    margin: 0;
`;

const LinkList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;

    li {
        font-size: 16px;
        opacity: 0.85;
    }
`;

const BadgeRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const PaymentBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    min-height: 32px;
    font-size: 0.875rem;
    font-weight: 600;
    opacity: 0.9;
    svg {
        width: 2rem;
        height: 2rem;
    }
`;

const Divider = styled.hr<{ $color?: string | null }>`
    border: none;
    border-top: 1px solid ${({ $color }) => $color ?? 'currentColor'};
    opacity: 0.2;
    margin: 32px 0 16px;
`;

const CopyrightBar = styled(Text)`
    color: rgba(153, 161, 175, 1);
    text-align: center;
    font-size: 14px;
    margin: 0;
    opacity: 0.7;
`;

export default Footer;
